import type { DocumentV1 } from '@alpod-ai-writer/backend';
import { JobQueueStatus } from '@alpod-ai-writer/backend/src/db/schema';
import type { DocumentConfig } from '@alpod-ai-writer/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cn } from '@udecode/cn';
import clsx from 'clsx';
import { Button, Checkbox, Modal, type ModalProps } from 'flowbite-react';
import { AlertTriangleIcon, CheckIcon, CircleSlashIcon, HourglassIcon, InfoIcon, LoaderCircleIcon, SparklesIcon } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useApi } from '../../../api/useApi.hook';
import { CreditConfirmationModal } from '../../../components/modals/creditConfirmation/creditConfirmationModal.component';
import { InsufficientCreditsModal } from '../../../components/modals/insufficientCredits/insufficientCreditsModal.component';
import { SHOW_RIGHT_PANEL_KEY } from '../../../editor/components/documentEditor.component';
import type { DocumentStructure } from '../../../editor/hooks/useDocumentStructure';
import { useLocalStorage } from '../../../hooks/useLocalStorage.hook';
import { useModals } from '../../../hooks/useModals.hook';
import { pathWithParams } from '../../../lib/routing.utils';
import { useCredits } from '../../hooks/useCredits.hook';
import { RouteIndex } from '../../routeIndex';
import { AiFeatureType } from '../../types/credit.types';

interface FormValues {
    type: 'basic'|'advanced';
    sections: Record<string, boolean>;
}

export interface AiProofreadModalProps extends ModalProps {
    document: DocumentV1.Record;
    documentStructure?: DocumentStructure;
    jobId?: string|null;
}

export const AiProofreadModal = ({
    document,
    jobId: initialJobId,
    documentStructure,
    show,
    onClose
}: AiProofreadModalProps) => {
    const api = useApi();
    const modals = useModals();
    const queryClient = useQueryClient();
    const { canUseFeature, getFeatureCost } = useCredits();

    const basicCostPerPage = getFeatureCost(AiFeatureType.PAGE_CORRECTION_BASIC);
    const advancedCostPerPage = getFeatureCost(AiFeatureType.PAGE_CORRECTION_ADVANCED);

    const [ isHidden, setIsHidden ] = useLocalStorage('hide-ai-proofreading-disclaimer', false);
    const [ confirmAnotherJob, setConfirmAnotherJob ] = useState(false);

    const sectionsToSelect = (document.template?.config as DocumentConfig)?.sections
        .filter((section) => {
            if (section.type !== 'content') return false;
            const charCount = documentStructure?.sections[section.id]?.characterCount ?? 0;
            return charCount / 1800 > 0.01;
        })
        .map((section) => section.id) ?? [];

    const [ jobId, setJobId ] = useState<string|null>(initialJobId ?? null);

    const existingJobs = useQuery({
        queryKey: ['documents', document.documentId, 'runningJobs'],
        queryFn: async () => {
            return api.v1.documents.getJobList(document.documentId!, {
                filter: [ 'and', [
                    [ 'or' , [
                        [ 'status', 'eq', JobQueueStatus.Pending ],
                        [ 'status', 'eq', JobQueueStatus.InProgress ]
                    ]]
                ]],
                limit: 30,
                offset: 0
            });
        }
    });

    const { register, handleSubmit, watch } = useForm<FormValues>({
        defaultValues: {
            type: 'basic',
            sections: sectionsToSelect.reduce((acc, sectionId) => {
                acc[sectionId] = true;
                return acc;
            }, {} as Record<string, boolean>)
        }
    });

    const watchedType = watch('type');
    const watchedSections = watch('sections');
    const costPerPage = watchedType === 'advanced' ? advancedCostPerPage : basicCostPerPage;

    const hideDisclaimer = () => {
        setIsHidden(true);
    };

    const createJob = useMutation({
        mutationFn: async (data: FormValues) => {
            const response = await api.v1.documents.aiProofread(document.documentId, {
                revisionId: document.currentRevisionId!,
                useLanguageTools: data.type === 'advanced',
                selectedSections: Object.entries(data.sections)
                    .filter(([, selected]) => selected)
                    .map(([ sectionId ]) => sectionId)
            });

            setJobId(response.jobId);
            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['wallet-balance'] });
            queryClient.invalidateQueries({ queryKey: ['credit-transactions'] });
        }
    });

    const job = useQuery({
        queryKey: ['document', document.documentId, 'aiProofreadJob', jobId],
        enabled: !!jobId,
        queryFn: async () => {
            const response = await api.v1.documents.getJobById(document.documentId, jobId!);
            return response;
        },
        refetchInterval: 2000
    });

    const config = document.template?.config as DocumentConfig;
    const progress = job.data?.progress as DocumentV1.AiProofreadingProgress|undefined;

    const doSubmit = useCallback((data: FormValues) => {
        // Calculate norm pages from selected sections
        const normPages = Object.entries(data.sections)
            .filter(([, selected]) => selected)
            .reduce((sum, [sectionId]) => { 
                const charCount = documentStructure?.sections[sectionId]?.characterCount ?? 0;

                if (charCount === 0) {
                    return sum;
                }

                const normPage = Math.ceil(charCount / 1800) || 0;
                return sum + normPage; 
            }, 0);
        const featureType = data.type === 'advanced' ? AiFeatureType.PAGE_CORRECTION_ADVANCED : AiFeatureType.PAGE_CORRECTION_BASIC;
        const costPerPage = data.type === 'advanced' ? advancedCostPerPage : basicCostPerPage;
        const creditCost = costPerPage * normPages;

        const { canUse, requiredCredits, currentBalance } = canUseFeature(featureType, normPages);

        if (!canUse) {
            modals.showModal('insufficient-credits-proofread', InsufficientCreditsModal, {
                currentBalance,
                requiredCredits,
                featureName: 'AI korektura',
                onTopUp: () => onClose?.(),
            });
            return;
        }

        modals.showModal('credit-confirm-proofread', CreditConfirmationModal, {
            creditCost,
            featureName: 'AI korektura',
            onConfirm: () => {
                createJob.mutate(data);
            }
        });
    }, [ document, documentStructure, canUseFeature, modals, createJob, basicCostPerPage, advancedCostPerPage ]);

    const handleOpenRevision = useCallback(() => {
        if (!progress?.newRevisionId) {
            return;
        }

        localStorage.setItem(SHOW_RIGHT_PANEL_KEY, JSON.stringify(true));

        onClose?.();
        location.href = pathWithParams(RouteIndex.documentDetail.editRevision, {
            documentId: document.documentId,
            revisionId: progress.newRevisionId
        }, 'intro');
    }, [ document, progress, onClose ]);

    const handleConfirmAnotherJob = useCallback(() => {
        setConfirmAnotherJob(true);
    }, []);

    const hasOtherJobsRunning = existingJobs.data?.items.some((j) => j.jobId !== jobId);

    return (
        <Modal show={show} size="xl" onClose={onClose} popup theme={{ content: { base: 'relative h-full w-full p-4 md:h-auto my-auto' } }}>
            <Modal.Header>
                <span className="flex flex-row items-start md:items-center leading-5 justify-center gap-2 p-3 text-base md:text-lg font-semibold text-gray-800 dark:text-gray-400">
                    <SparklesIcon size={20} className='flex-shrink-0' />
                    <span>AI korektura dokumentu</span>
                </span>
            </Modal.Header>
            <Modal.Body>
                <div className='flex flex-col gap-4'>
                    {!isHidden && (
                        <div className='flex flex-col gap-4 text-left bg-blue-100 p-4 text-sm mb-4 rounded-md border border-blue-300'>
                            <h3 className='flex flex-row items-center gap-2 text-md text-gray-800 font-semibold'><InfoIcon className='w-4 h-4' /> Jak funguje AI korektura?</h3>
                            <p>AI korektura používá velké jazykové modely. Přestože má AI agent k dispozici nástroje pro jazykovou analýzu a vyhledávání v jazykové příručce, může dělat chyby.</p>
                            <p><strong>Vždy doporučujeme výstup zkontrolovat lidským korektorem.</strong></p>
                            <p>AI korektura bude provedena nad verzí dokumentu v okamžiku spuštění. Další změny v dokumentu provedené po zahájení korektury již nebudou zahrnuty. Po dokončení korektury bude vytvořena nová revize dokumentu, ve které budete mít k dispozici návrhy změn.</p>
                            <p className='text-right'>
                                <button
                                    className='p-0 underline hover:no-underline text-primary-500 hover:text-primary-700'
                                    onClick={hideDisclaimer}
                                >Rozumím, již nezobrazovat</button>
                            </p>
                        </div>
                    )}
                    {(hasOtherJobsRunning && !progress && !confirmAnotherJob) && (
                        <div className='flex flex-col gap-2 bg-orange-50 p-4 text-sm rounded-md border border-orange-300'>
                            <p className='font-semibold text-center'>Korektura tohoto dokumentu již probíhá, opravdu chcete provést další?</p>
                            <div className='flex flex-row gap-2 mt-2'>
                                <Button
                                    type='button'
                                    onClick={onClose}
                                    color='gray'
                                    className='flex-1'
                                >Zrušit</Button>
                                <Button
                                    type='button'
                                    onClick={handleConfirmAnotherJob}
                                    color='primary'
                                    className='flex-1'
                                >Ano, pokračovat</Button>
                            </div>
                        </div>
                    )}
                    {!jobId && (
                        <form
                            id='proofread-form'
                            onSubmit={handleSubmit(doSubmit)}
                            className={clsx(
                                'flex flex-col gap-4',
                                (!existingJobs.data || hasOtherJobsRunning) && !confirmAnotherJob && 'pointer-events-none opacity-50'
                            )}
                        >
                            <h2 className='text-center font-semibold text-base'>Vyberte typ korektury</h2>

                            <div className="flex flex-col gap-0 rounded-md border border-gray-200 bg-gray-50 overflow-hidden">
                                <label className="flex flex-row items-start gap-3 cursor-pointer px-4 py-3 transition-colors hover:bg-gray-100">
                                    <input
                                        type="radio"
                                        value="basic"
                                        {...register('type')}
                                        className="mt-1"
                                        id="basicProofread"
                                    />
                                    <div>
                                        <span className="font-medium">Základní korektura</span>
                                        <div className="text-xs text-gray-500 mt-0.5">
                                            Rychlá AI kontrola zaměřená na pravopis, interpunkci a překlepy. Ideální pro rychlou kontrolu textu bez hlubších zásahů do stylu.
                                        </div>
                                    </div>
                                </label>
                                <div className="border-t border-gray-200" />
                                <label className="flex flex-row items-start gap-3 cursor-pointer px-4 py-3 transition-colors hover:bg-gray-100">
                                    <input
                                        type="radio"
                                        value="advanced"
                                        {...register('type')}
                                        className="mt-1"
                                        id="advancedProofread"
                                    />
                                    <div>
                                        <span className="font-medium">Pokročilá korektura</span>
                                        <div className="text-xs text-gray-500 mt-0.5">
                                            Velmi podrobná korektura dle pravidel Internetové jazykové příručky Ústavu pro jazyk český AV ČR. Kromě pravopisu a interpunkce upravuje styl, plynulost a srozumitelnost textu tak, aby odpovídal spisovné normě a působil profesionálně.                                        </div>
                                    </div>
                                </label>
                            </div>

                            <h2 className='text-center font-semibold text-base'>Vyberte sekce dokumentu ke korektuře</h2>

                            <div className='relative flex flex-col items-stretch gap-[1px] overflow-x-hidden overflow-y-auto max-h-[calc(90vh-16rem)]'>
                                {config && (
                                    <>
                                        {config.sections
                                            .filter((section) => {
                                                if (section.type !== 'content') return false;
                                                const charCount = documentStructure?.sections[section.id]?.characterCount ?? 0;
                                                return charCount / 1800 > 0.01;
                                            })
                                            .map((section) => {
                                                const charCount = documentStructure?.sections[section.id]?.characterCount ?? 0;
                                                const normPages = charCount / 1800;

                                                return (
                                                    <label
                                                        key={section.id}
                                                        className='flex flex-row items-center gap-2 first:rounded-t-md px-3 py-2 text-sm font-medium bg-gray-100'
                                                    >
                                                        <Checkbox
                                                            id="useTools"
                                                            {...register(`sections.${section.id}`, {
                                                                value: true
                                                            })}
                                                            color='primary'
                                                        />
                                                        <span className='flex-1 overflow-hidden overflow-ellipsis whitespace-nowrap'>
                                                            {section.title}
                                                        </span>
                                                        {normPages > 0.01 && (
                                                            <span className='text-xs text-gray-500 flex-shrink-0'>
                                                                {Math.ceil(normPages)} NS - {Math.ceil(normPages) * costPerPage} kreditů
                                                            </span>
                                                        )}
                                                    </label>
                                                );
                                            })}
                                    </>
                                )}
                            </div>

                            {watchedSections && (() => {
                                const totalNormPages = Object.entries(watchedSections)
                                    .filter(([, selected]) => selected)
                                    .reduce((sum, [sectionId]) => {
                                        const charCount = documentStructure?.sections[sectionId]?.characterCount ?? 0;
                                        if (charCount === 0) return sum;
                                        return sum + Math.ceil(charCount / 1800);
                                    }, 0);
                                const totalCredits = totalNormPages * costPerPage;
                                return (
                                    <p className='text-sm font-semibold text-center mt-2'>
                                        Suma za AI korekturu dokumentu - {totalNormPages} normostran - {totalCredits} kreditů
                                    </p>
                                );
                            })()}
                        </form>
                    )}
                    {!!job.data && (
                        <div className='relative flex flex-col items-stretch gap-[1px]'>
                            {config && (
                                <>
                                    {config.sections
                                        .filter((section) => {
                                            if (section.type !== 'content') return false;
                                            if (!documentStructure) return true;
                                            const charCount = documentStructure.sections[section.id]?.characterCount ?? 0;
                                            return charCount / 1800 > 0.01;
                                        })
                                        .map((section) => {
                                            const sectionProgress = progress?.sections[section.id] ?? {
                                                chunkCount: 0,
                                                processedCount: 0,
                                                processingCount: 0,
                                                errorCount: 0
                                            };

                                            const isPending = job.data.status === JobQueueStatus.Pending;
                                            const hasWork = sectionProgress?.chunkCount > 0;
                                            const isProcessing = hasWork && sectionProgress?.processingCount > 0;
                                            const isDone = hasWork && sectionProgress?.processedCount === sectionProgress?.chunkCount;
                                            const isWaiting = hasWork && !isProcessing && !isDone;
                                            const processedIndex = (sectionProgress?.processedCount ?? 0) + (sectionProgress?.errorCount ?? 0);
                                            const processingIndex = processedIndex + (sectionProgress?.processingCount ?? 0);

                                            const bars: Array<'done'|'processing'|'waiting'> = Array.from({ length: sectionProgress?.chunkCount }, (_, i) => {
                                                if (i < processedIndex) {
                                                    return 'done';
                                                }
                                                if (i < processingIndex) {
                                                    return 'processing';
                                                }
                                                return 'waiting';
                                            });

                                            return (
                                                <div
                                                    key={section.id}
                                                    className='flex flex-row items-center gap-2 first:rounded-t-md px-3 py-2 text-sm font-medium bg-gray-100'
                                                >
                                                    {!isPending && isDone && (
                                                        <CheckIcon className='text-lime-700' size={16} />
                                                    )}
                                                    {!isPending && isProcessing && (
                                                        <LoaderCircleIcon className='text-gray-600 animate-spin' size={16} />
                                                    )}
                                                    {(isWaiting || isPending) && (
                                                        <HourglassIcon className='text-gray-600' size={16} />
                                                    )}
                                                    {!hasWork && !isPending && (
                                                        <CircleSlashIcon className='text-gray-400' size={16} />
                                                    )}
                                                    <span className='overflow-hidden overflow-ellipsis whitespace-nowrap w-1/2 mr-2'>
                                                        {section.title}
                                                    </span>
                                                    <span className='flex flex-row items-stretch gap-[2px] rounded-full flex-grow h-2 overflow-hidden'>
                                                        {bars.map((bar, i) => (
                                                            <span
                                                                key={i}
                                                                className={cn(
                                                                    'flex-grow h-full transition-colors duration-500',
                                                                    bar === 'done' && 'bg-lime-500',
                                                                    bar === 'processing' && 'bg-gray-400',
                                                                    bar === 'waiting' && 'bg-gray-300'
                                                                )}
                                                            />
                                                        ))}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                </>
                            )}
                            {(job.data?.status === JobQueueStatus.Pending) && (
                                <div className='sticky bottom-0 border-t border-white flex flex-row items-center justify-center gap-2 rounded-b-md bg-gray-100 px-4 py-3 text-sm font-medium w-full'>
                                    <LoaderCircleIcon className='animate-spin text-primary-500' size={24} />
                                    <span>Korektura čeká na zpracování ve frontě...</span>
                                </div>
                            )}
                            {(job.data?.status === JobQueueStatus.InProgress) && (
                                <div className='sticky bottom-0 border-t border-white flex flex-row items-center justify-center gap-2 rounded-b-md bg-gray-100 px-4 py-3 text-sm font-medium w-full'>
                                    <LoaderCircleIcon className='animate-spin text-primary-500' size={24} />
                                    <span>Probíhá korektura...</span>
                                </div>
                            )}
                            {job.data?.status === JobQueueStatus.Failed && (
                                <div className='sticky bottom-0 border-t border-white flex flex-col items-center justify-center gap-2 rounded-b-md bg-gray-100 px-4 py-4 text-sm font-medium w-full'>
                                    <div className='flex flex-row items-center gap-2'>
                                        <AlertTriangleIcon className='text-red-700' size={24} />
                                        <span className='text-red-900'>Při provádění korektury došlo k chybě.</span>
                                    </div>
                                </div>
                            )}

                        </div>
                    )}
                    {job.data?.status === JobQueueStatus.Completed && progress && (
                        <div className='flex flex-col items-center justify-center gap-2 rounded-md bg-gray-100 px-4 py-4 text-sm font-medium w-full'>
                            <div className='flex flex-row items-center gap-2'>
                                <CheckIcon className='text-lime-700' size={24} />
                                <span className='text-lime-900'>Korektura byla dokončena</span>
                            </div>
                            {(progress.featureUsage.languageAnalysis > 0 || progress.featureUsage.languageManual > 0) && (
                                <div className='text-xs text-gray-600 flex flex-col items-center gap-1 mt-1'>
                                    {progress.featureUsage.languageAnalysis > 0 && (
                                        <span>Analýza jazykových jevů provedena: <span className="font-medium">{progress.featureUsage.languageAnalysis}x</span></span>
                                    )}
                                    {progress.featureUsage.languageManual > 0 && (
                                        <span>Jazyková příručka použita: <span className="font-medium">{progress.featureUsage.languageManual}x</span></span>
                                    )}
                                    {progress.featureUsage.autoFormatting > 0 && (
                                        <span>Automatické formátování použito: <span className="font-medium">{progress.featureUsage.autoFormatting}x</span></span>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    {progress?.newRevisionId && (
                        <>
                            <div className='flex justify-center gap-4'>
                                <Button
                                    type='button'
                                    color='primary'
                                    onClick={handleOpenRevision}
                                >
                                    Otevřít opravený dokument
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </Modal.Body>

            {!jobId && (
                <>
                    <p className='ml-4 mr-4 mt-4 mb-4 text-sm text-center'><i>Cena za 1 normostranu je {basicCostPerPage} kreditů (základní) nebo {advancedCostPerPage} kreditů (pokročilá). Kredity se odečítají za každou započatou normostranu (např. 0,5 normostrany = 1 normostrana</i></p>
                    <p className='ml-4 mr-4 mt-4 mb-4 text-sm font-semibold text-center'>Provedení korektury může trvat 5 - 15 minut dle rozsahu práce a&nbsp;vytížení služby.</p>
                    <div className="mb-4 flex justify-center gap-4">
                        <Button
                            type='submit'
                            form='proofread-form'
                            color="primary"
                            disabled={createJob.isPending}
                        >
                            {createJob.isError ? 'Zkusit znovu' : 'Provést korekturu'}
                        </Button>
                    </div>
                </>
            )}
        </Modal>
    );
};
